/**
 * useAutoChartTitle Hook
 *
 * Automatically generates and updates chart titles based on data changes.
 * Integrates with compound charts to update the title component when data changes.
 *
 * Features:
 * - Detects data changes on chart components
 * - Extracts field names from chart data
 * - Finds the associated title component in the component tree
 * - Updates title via editor store
 * - Respects manual title overrides (autoTitle: false)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { chartTitleGenerator } from '@/services/ai/charts/ChartTitleGenerator';
import { logger } from '@/utils/logger';
import type { Component } from '@/types';
import type { ChartDataPoint } from '@/services/ai/charts/ChartDataManager';

interface UseAutoChartTitleOptions {
  /** The chart component ID */
  chartId: string;
  /** Chart type (line, bar, area) */
  chartType: 'line' | 'bar' | 'area';
  /** Current chart data */
  data: ChartDataPoint[];
  /** Whether auto-title is enabled (default: true) */
  enabled?: boolean;
}

interface UseAutoChartTitleResult {
  /** Manually trigger title generation */
  generateTitle: () => Promise<void>;
  /** Whether a title generation is in progress */
  isGenerating: boolean;
  /** The last generated title */
  lastTitle: string | null;
}

/**
 * Hook for automatically generating chart titles based on data
 */
export function useAutoChartTitle({
  chartId,
  chartType,
  data,
  enabled = true
}: UseAutoChartTitleOptions): UseAutoChartTitleResult {
  const isGeneratingRef = useRef(false);
  const lastTitleRef = useRef<string | null>(null);
  const lastDataHashRef = useRef<string>('');

  // Get store methods
  const getComponent = useEditorStore(state => state.getComponent);
  const components = useEditorStore(state => state.components);
  const updateComponent = useEditorStore(state => state.updateComponent);

  /**
   * Find the title component for a compound chart
   * Compound chart structure:
   *   compound-*-wrapper (flex-container)
   *   ├── compound-*-header (flex-container)
   *   │   ├── compound-*-title (text) ← This is what we're looking for
   *   │   └── compound-*-value (data-text)
   *   └── compound-*-chart (*-chart)
   */
  const findTitleComponent = useCallback((chartComponentId: string): Component | null => {
    const chartComponent = getComponent(chartComponentId);
    if (!chartComponent) return null;

    // Get the wrapper (parent of the chart)
    const wrapper = chartComponent.parentId
      ? getComponent(chartComponent.parentId)
      : null;

    if (!wrapper) return null;

    // Find the header container (sibling of chart, child of wrapper)
    const headerComponent = components.find(c =>
      c.parentId === wrapper.id &&
      c.type === 'flex-container' &&
      (c.id.includes('header') || c.name?.toLowerCase().includes('header'))
    );

    if (!headerComponent) return null;

    // Find the title component (child of header, type text)
    const titleComponent = components.find(c =>
      c.parentId === headerComponent.id &&
      c.type === 'text' &&
      (c.id.includes('title') || c.name?.toLowerCase().includes('title'))
    );

    return titleComponent || null;
  }, [getComponent, components]);

  /**
   * Extract unique field names from chart data
   */
  const extractFieldNames = useCallback((chartData: ChartDataPoint[]): string[] => {
    if (!chartData || chartData.length === 0) return [];

    const fieldSet = new Set<string>();
    chartData.forEach(point => {
      Object.keys(point).forEach(key => fieldSet.add(key));
    });

    return Array.from(fieldSet);
  }, []);

  /**
   * Check if the title was manually set (autoTitle === false)
   */
  const isManuallySet = useCallback((titleComponent: Component): boolean => {
    return titleComponent.properties?.autoTitle === false;
  }, []);

  /**
   * Generate a simple hash of the data for change detection
   */
  const getDataHash = useCallback((chartData: ChartDataPoint[]): string => {
    const fieldNames = extractFieldNames(chartData);
    return fieldNames.sort().join(',');
  }, [extractFieldNames]);

  /**
   * Generate and update the title
   */
  const generateTitle = useCallback(async () => {
    if (!enabled || isGeneratingRef.current) return;

    const titleComponent = findTitleComponent(chartId);
    if (!titleComponent) {
      console.debug('[useAutoChartTitle] No title component found for chart', chartId);
      return;
    }

    // Skip if title was manually set
    if (isManuallySet(titleComponent)) {
      console.debug('[useAutoChartTitle] Skipping auto-title for manually set title', chartId);
      return;
    }

    const fieldNames = extractFieldNames(data);
    if (fieldNames.length === 0) {
      console.debug('[useAutoChartTitle] No field names to generate title from', chartId);
      return;
    }

    isGeneratingRef.current = true;

    try {
      const result = await chartTitleGenerator.generateTitle(
        chartId,
        fieldNames,
        chartType,
        data.slice(0, 3) // Pass sample data
      );

      if (result.title && result.title !== lastTitleRef.current) {
        // Update the title component
        updateComponent(titleComponent.id, {
          properties: {
            ...titleComponent.properties,
            content: result.title,
            autoTitle: true // Mark as auto-generated
          }
        });

        lastTitleRef.current = result.title;

        console.debug('[useAutoChartTitle] Updated chart title', {
          chartId,
          title: result.title,
          confidence: result.confidence,
          cached: result.cached
        });
      }
    } catch (error) {
      console.error('[useAutoChartTitle] Failed to generate title', error);
    } finally {
      isGeneratingRef.current = false;
    }
  }, [
    enabled,
    chartId,
    chartType,
    data,
    findTitleComponent,
    isManuallySet,
    extractFieldNames,
    updateComponent
  ]);

  /**
   * Effect to detect data changes and trigger title generation
   */
  useEffect(() => {
    if (!enabled || !data || data.length === 0) return;

    const currentHash = getDataHash(data);

    // Only regenerate if field names changed (not just values)
    if (currentHash !== lastDataHashRef.current) {
      lastDataHashRef.current = currentHash;

      // Debounce is handled by the ChartTitleGenerator
      generateTitle();
    }
  }, [data, enabled, getDataHash, generateTitle]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      chartTitleGenerator.cancelPending(chartId);
    };
  }, [chartId]);

  return {
    generateTitle,
    isGenerating: isGeneratingRef.current,
    lastTitle: lastTitleRef.current
  };
}

/**
 * Event-based title generation trigger
 * Can be used by ChartDataManager to emit title generation events
 */
export interface ChartTitleEvent {
  chartId: string;
  fieldNames: string[];
  chartType: 'line' | 'bar' | 'area';
  sampleData?: ChartDataPoint[];
  /** Name of the data source/schema for better title context */
  dataSourceName?: string;
}

type ChartTitleEventHandler = (event: ChartTitleEvent) => void;

class ChartTitleEventEmitter {
  private listeners: ChartTitleEventHandler[] = [];

  subscribe(handler: ChartTitleEventHandler): () => void {
    this.listeners.push(handler);
    return () => {
      this.listeners = this.listeners.filter(h => h !== handler);
    };
  }

  emit(event: ChartTitleEvent): void {
    this.listeners.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('[ChartTitleEventEmitter] Handler error', error);
      }
    });
  }
}

export const chartTitleEvents = new ChartTitleEventEmitter();

/**
 * Global hook to subscribe to chart title events
 * Should be used at the app level to handle title updates for all charts
 */
export function useChartTitleEventListener() {
  // Use refs to avoid dependency array issues that cause frequent re-subscriptions
  const storeRef = useRef(useEditorStore.getState());

  // Keep store ref updated
  useEffect(() => {
    return useEditorStore.subscribe(state => {
      storeRef.current = state;
    });
  }, []);

  useEffect(() => {
    const handleTitleEvent = async (event: ChartTitleEvent) => {
      const { chartId, fieldNames, chartType, sampleData, dataSourceName } = event;
      const store = storeRef.current;

      logger.debug("[AutoChartTitle] Event received", {
        chartId,
        fieldNames,
        chartType,
        dataSourceName,
      });

      // Find the chart component
      const chartComponent = store.getComponent(chartId);
      if (!chartComponent) {
        logger.debug("[AutoChartTitle] Chart not found", { chartId });
        return;
      }

      logger.debug("[AutoChartTitle] Chart found", {
        id: chartComponent.id,
        type: chartComponent.type,
        parentId: chartComponent.parentId
      });

      // Find the wrapper (parent of the chart)
      const wrapper = chartComponent.parentId
        ? store.getComponent(chartComponent.parentId)
        : null;

      if (!wrapper) {
        logger.debug("[AutoChartTitle] Wrapper not found for chart", { chartId });
        return;
      }

      logger.debug("[AutoChartTitle] Wrapper found", {
        id: wrapper.id,
        type: wrapper.type,
        name: wrapper.name
      });

      // Find the header container among all components
      const components = store.components;
      const headerComponent = components.find(c =>
        c.parentId === wrapper.id &&
        c.type === 'flex-container' &&
        (c.id.includes('header') || c.name?.toLowerCase().includes('header'))
      );

      if (!headerComponent) {
        logger.debug("[AutoChartTitle] Header not found", {
          chartId,
          wrapperChildren: components
            .filter(c => c.parentId === wrapper.id)
            .map(c => ({
              id: c.id,
              type: c.type,
              name: c.name
            })),
        });
        return;
      }

      logger.debug("[AutoChartTitle] Header found", {
        id: headerComponent.id,
        type: headerComponent.type,
        name: headerComponent.name
      });

      // Find the title component
      const titleComponent = components.find(c =>
        c.parentId === headerComponent.id &&
        c.type === 'text' &&
        (c.id.includes('title') || c.name?.toLowerCase().includes('title'))
      );

      if (!titleComponent) {
        logger.debug("[AutoChartTitle] Title component not found", {
          chartId,
          headerChildren: components
            .filter(c => c.parentId === headerComponent.id)
            .map(c => ({
              id: c.id,
              type: c.type,
              name: c.name
            })),
        });
        return;
      }

      logger.debug("[AutoChartTitle] Title component found", {
        id: titleComponent.id,
        type: titleComponent.type,
        name: titleComponent.name,
        content: titleComponent.properties?.content
      });

      // Check if title was manually set
      if (titleComponent.properties?.autoTitle === false) {
        logger.debug("[AutoChartTitle] Title manually set, skipping", { chartId });
        return;
      }

      try {
        // Generate new title
        logger.debug("[AutoChartTitle] Generating title", { chartId });
        const result = await chartTitleGenerator.generateTitle(
          chartId,
          fieldNames,
          chartType,
          sampleData,
          dataSourceName
        );

        if (result.title) {
          // Update the title component
          store.updateComponent(titleComponent.id, {
            properties: {
              ...titleComponent.properties,
              content: result.title,
              autoTitle: true
            }
          });

          logger.debug("[AutoChartTitle] Updated title", {
            chartId,
            title: result.title,
            confidence: result.confidence,
            cached: result.cached
          });
        }
      } catch (error) {
        logger.error("[AutoChartTitle] Failed to generate title", error);
      }
    };

    // Subscribe to events
    logger.debug("[AutoChartTitle] Subscribing to chart title events");
    const unsubscribe = chartTitleEvents.subscribe(handleTitleEvent);

    return () => {
      logger.debug("[AutoChartTitle] Unsubscribing from chart title events");
      unsubscribe();
    };
  }, []); // Empty dependency array - subscribe only once
}
